import { cn } from '@/lib/utils';

/**
 * Left-packed label/value list used by the Job Cost sidebar panels.
 *
 * Panels previously used `flex justify-between` per row, which pinned the label
 * to the left edge and the value to the right edge of whatever width the panel
 * happened to have. That stretched every panel to full sidebar width and opened
 * a gap in the middle, which is what the client asked us to remove ("left
 * justify the boxes so we can use less room and fit more on one screen"
 * — Angie, Note 1).
 *
 * Here the two columns are `auto`-sized and the grid is packed to the start, so
 * a panel is only as wide as its widest label plus its widest value. Values are
 * right-aligned *within their own column* and use tabular figures, so decimal
 * points still line up down the column — packing left must not cost us readable
 * money.
 */
export function SummaryList({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <dl
      className={cn(
        'grid grid-cols-[auto_auto] justify-start gap-x-4 gap-y-1 text-xs',
        className
      )}
    >
      {children}
    </dl>
  );
}

export interface SummaryRowProps {
  label: string;
  value: React.ReactNode;
  /** Renders as a total row: bolder, with a rule above spanning both columns. */
  emphasis?: boolean;
  valueClassName?: string;
}

/**
 * A single label/value pair. Renders as two grid children (not a wrapper
 * element) so every row shares the parent {@link SummaryList} column tracks and
 * the values stay aligned with each other.
 */
export function SummaryRow({ label, value, emphasis, valueClassName }: SummaryRowProps) {
  return (
    <>
      {/* A total's rule spans both columns as one grid item. Bordering the two
          cells individually would leave a gap in the middle of the line, since
          the columns are separated by gap-x. */}
      {emphasis && <div aria-hidden className="col-span-2 border-t border-border mt-1" />}
      <dt className={cn(emphasis ? 'font-semibold' : 'text-muted-foreground')}>
        {label}
      </dt>
      <dd
        className={cn(
          'text-right tabular-nums',
          emphasis ? 'font-bold' : 'font-medium',
          valueClassName
        )}
      >
        {value}
      </dd>
    </>
  );
}
